import { useEffect, useState } from 'react';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

const EMPTY_FORM = {
  first_name: '', last_name: '', dob: '', email: '', phone: '',
  address: '', suburb: '', state: '', postcode: '',
  membership_type: 'family',
  payment_status: 'paid',
  spouse_name: '', children: '',
};

function normaliseType(t) {
  if (!t) return 'family';
  if (t === 'membership_family' || t === 'family') return 'family';
  if (t === 'membership_single' || t === 'single') return 'single';
  return 'family';
}

function toApiType(t) {
  if (t === 'single') return 'membership_single';
  return 'membership_family';
}

function memberToForm(m) {
  return {
    first_name: m.first_name || '',
    last_name: m.last_name || '',
    dob: m.dob || '',
    email: m.email || '',
    phone: m.phone || '',
    address: m.address || '',
    suburb: m.suburb || '',
    state: m.state || '',
    postcode: m.postcode || '',
    membership_type: normaliseType(m.membership_type),
    payment_status: m.payment_status || 'paid',
    spouse_name: m.spouse_name || '',
    children: Array.isArray(m.children) ? m.children.join(', ') : (m.children || ''),
  };
}

function MemberFormFields({ values, onChange }) {
  const f = (k) => ({ value: values[k], onChange: e => onChange(k, e.target.value) });
  const isFamily = values.membership_type === 'family';
  return (
    <div className="row g-3">
      <div className="col-md-3"><label className="form-label small fw-bold">First Name *</label><input className="form-control" {...f('first_name')} required /></div>
      <div className="col-md-3"><label className="form-label small fw-bold">Last Name *</label><input className="form-control" {...f('last_name')} required /></div>
      <div className="col-md-3"><label className="form-label small fw-bold">Date of Birth</label><input type="date" className="form-control" {...f('dob')} /></div>
      <div className="col-md-3"><label className="form-label small fw-bold">Email *</label><input type="email" className="form-control" {...f('email')} required /></div>
      <div className="col-md-3"><label className="form-label small fw-bold">Phone</label><input className="form-control" {...f('phone')} /></div>
      <div className="col-md-5"><label className="form-label small fw-bold">Address</label><input className="form-control" {...f('address')} /></div>
      <div className="col-md-3"><label className="form-label small fw-bold">Suburb</label><input className="form-control" {...f('suburb')} /></div>
      <div className="col-md-2"><label className="form-label small fw-bold">State</label><input className="form-control" {...f('state')} /></div>
      <div className="col-md-2"><label className="form-label small fw-bold">Postcode</label><input className="form-control" {...f('postcode')} /></div>
      <div className="col-md-4">
        <label className="form-label small fw-bold">Membership Type</label>
        <div className="d-flex gap-3 pt-1">
          {[['single', 'Single'], ['family', 'Family']].map(([val, label]) => (
            <div key={val} className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id={`mtype_${val}`}
                name="membership_type"
                value={val}
                checked={values.membership_type === val}
                onChange={() => onChange('membership_type', val)}
              />
              <label className="form-check-label" htmlFor={`mtype_${val}`}>{label}</label>
            </div>
          ))}
        </div>
      </div>
      <div className="col-md-2">
        <label className="form-label small fw-bold">Payment Status</label>
        <select className="form-select" {...f('payment_status')}>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
        </select>
      </div>
      {isFamily && (
        <>
          <div className="col-md-3"><label className="form-label small fw-bold">Spouse Name</label><input className="form-control" {...f('spouse_name')} /></div>
          <div className="col-md-5"><label className="form-label small fw-bold">Children</label><input className="form-control" placeholder="Comma-separated names" {...f('children')} /></div>
        </>
      )}
    </div>
  );
}

export default function MembersManager() {
  const { admin } = useAuth();
  const isAdmin = (admin?.role || 'admin') === 'admin';
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  // Edit state
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteStep, setDeleteStep] = useState(0);
  const [deleting, setDeleting] = useState(false);

  // Sort state
  const [sortField, setSortField] = useState('first_name');
  const [sortDir, setSortDir] = useState('asc');

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const load = () => {
    setLoading(true);
    api.get('/members').then(r => setMembers(r.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const addMember = async (e) => {
    e.preventDefault();
    setSaving(true); setFormError('');
    try {
      const payload = { ...form, membership_type: toApiType(form.membership_type), children: form.children ? form.children.split(',').map(s => s.trim()).filter(Boolean) : [] };
      await api.post('/members/admin-add', payload);
      setShowAddForm(false);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const openEdit = (m) => { setEditingMember(m); setEditForm(memberToForm(m)); setEditError(''); };
  const closeEdit = () => { setEditingMember(null); setEditForm(EMPTY_FORM); setEditError(''); };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEditSaving(true); setEditError('');
    try {
      const payload = { ...editForm, membership_type: toApiType(editForm.membership_type), children: editForm.children ? editForm.children.split(',').map(s => s.trim()).filter(Boolean) : [] };
      await api.put(`/members/${editingMember.id}`, payload);
      closeEdit();
      load();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to save');
    } finally { setEditSaving(false); }
  };

  const startDelete = (m) => { setDeleteTarget(m); setDeleteStep(1); };
  const cancelDelete = () => { setDeleteTarget(null); setDeleteStep(0); };

  const confirmDelete = async () => {
    if (deleteStep === 1) { setDeleteStep(2); return; }
    setDeleting(true);
    try {
      await api.delete(`/members/${deleteTarget.id}`);
      cancelDelete();
      load();
    } catch (err) {
      cancelDelete();
    } finally { setDeleting(false); }
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  const filtered = members
    .filter(m => {
      const matchStatus = filter === 'all' || m.payment_status === filter;
      const q = search.toLowerCase();
      const matchSearch = !q || `${m.first_name} ${m.last_name} ${m.email}`.toLowerCase().includes(q);
      return matchStatus && matchSearch;
    })
    .sort((a, b) => {
      let va, vb;
      if (sortField === 'first_name') {
        va = `${a.first_name} ${a.last_name}`.toLowerCase();
        vb = `${b.first_name} ${b.last_name}`.toLowerCase();
      } else {
        va = a[sortField] || '';
        vb = b[sortField] || '';
      }
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const counts = {
    all: members.length,
    paid: members.filter(m => m.payment_status === 'paid').length,
    pending: members.filter(m => m.payment_status === 'pending').length,
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-0">Members</h4>
          <p className="text-muted small mb-0">Family membership registrations (2-year validity)</p>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={load}><i className="fas fa-sync-alt me-1"></i>Refresh</button>
          {isAdmin && <button className="btn btn-primary rounded-pill px-4" onClick={() => { setShowAddForm(v => !v); setFormError(''); }}><i className="fas fa-plus me-2"></i>Add Member</button>}
        </div>
      </div>

      {showAddForm && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-3">Add New Member</h5>
            {formError && <div className="alert alert-danger py-2">{formError}</div>}
            <form onSubmit={addMember}>
              <MemberFormFields values={form} onChange={(k, v) => setForm(f => ({ ...f, [k]: v }))} />
              <div className="d-flex gap-2 mt-4">
                <button type="submit" disabled={saving} className="btn btn-primary rounded-pill px-5">{saving ? 'Saving…' : 'Add Member'}</button>
                <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => { setShowAddForm(false); setForm(EMPTY_FORM); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="row g-3 mb-4">
        {[
          { label: 'Total', value: counts.all, icon: 'fa-id-card', color: 'primary' },
          { label: 'Paid', value: counts.paid, icon: 'fa-check-circle', color: 'success' },
          { label: 'Pending', value: counts.pending, icon: 'fa-clock', color: 'warning' },
        ].map(s => (
          <div key={s.label} className="col-4">
            <div className="bg-white rounded p-3 shadow-sm d-flex align-items-center gap-3">
              <div className={`btn-square bg-${s.color} bg-opacity-10 rounded text-${s.color}`}><i className={`fas ${s.icon}`}></i></div>
              <div><div className="fw-bold fs-5">{s.value}</div><div className="text-muted small">{s.label}</div></div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded shadow-sm p-3 mb-3 d-flex gap-3 align-items-center flex-wrap">
        <input className="form-control form-control-sm" style={{ maxWidth: 220 }} placeholder="Search name or email…" value={search} onChange={e => setSearch(e.target.value)} />
        <div className="btn-group btn-group-sm ms-auto">
          {['all', 'paid', 'pending'].map(f => (
            <button key={f} className={`btn ${filter === f ? 'btn-primary' : 'btn-outline-secondary'} rounded-pill px-3`} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)} ({counts[f] ?? 0})
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-5 text-muted"><span className="spinner-border spinner-border-sm me-2"></span>Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-5 text-muted"><i className="fas fa-id-card fa-2x mb-2 d-block opacity-25"></i>No members found</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  {[
                    { label: 'Name', field: 'first_name' },
                    { label: 'Email', field: null },
                    { label: 'Type', field: null },
                    { label: 'Status', field: null },
                    { label: 'Expires', field: 'expires_at' },
                    { label: 'Registered', field: 'created_at' },
                  ].map(({ label, field }) => (
                    <th key={label}
                      className="small text-muted fw-semibold"
                      style={field ? { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' } : {}}
                      onClick={field ? () => toggleSort(field) : undefined}
                    >
                      {label}
                      {field && (
                        <i className={`fas ms-1 ${sortField === field ? (sortDir === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'}`}
                          style={{ opacity: sortField === field ? 1 : 0.35, fontSize: '0.75rem' }}></i>
                      )}
                    </th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(m => (
                  <tr key={m.id}>
                    <td>
                      <div className="fw-semibold">{m.first_name} {m.last_name}</div>
                      {m.membership_no && <div className="text-muted small">{m.membership_no}</div>}
                      {m.suburb && <div className="text-muted small">{m.suburb}, {m.state}</div>}
                    </td>
                    <td className="small">{m.email}<br /><span className="text-muted">{m.phone}</span></td>
                    <td>
                      <span className={`badge ${normaliseType(m.membership_type) === 'single' ? 'badge-soft-info' : 'badge-soft-purple'}`}>
                        {normaliseType(m.membership_type) === 'single' ? 'Single' : 'Family'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${m.payment_status === 'paid' ? 'badge-soft-success' : 'badge-soft-warning'}`}>
                        {m.payment_status === 'paid' ? 'Paid' : 'Pending'}
                      </span>
                    </td>
                    <td className="small text-muted">{fmtDate(m.expires_at)}</td>
                    <td className="small text-muted">{fmtDate(m.created_at)}</td>
                    <td className="text-nowrap">
                      <button className="btn btn-sm btn-outline-primary rounded-pill me-1" title="View" onClick={() => setSelected(m)}><i className="fas fa-eye"></i></button>
                      {isAdmin && <button className="btn btn-sm btn-outline-secondary rounded-pill me-1" title="Edit" onClick={() => openEdit(m)}><i className="fas fa-pencil-alt"></i></button>}
                      {isAdmin && <button className="btn btn-sm btn-outline-danger rounded-pill" title="Delete" onClick={() => startDelete(m)}><i className="fas fa-trash"></i></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── View detail modal ── */}
      {selected && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={() => setSelected(null)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Member Detail</h5>
                <button className="btn-close" onClick={() => setSelected(null)}></button>
              </div>
              <div className="modal-body">
                <dl className="row mb-0">
                  {[
                    ['Membership No', selected.membership_no || '—'],
                    ['Name', `${selected.first_name} ${selected.last_name}`],
                    ['Date of Birth', selected.dob ? new Date(selected.dob + 'T00:00:00').toLocaleDateString('en-AU') : '—'],
                    ['Email', selected.email],
                    ['Phone', selected.phone],
                    ['Address', [selected.address, selected.suburb, selected.state, selected.postcode].filter(Boolean).join(', ')],
                    ['Type', normaliseType(selected.membership_type) === 'single' ? 'Single Membership (2 Years)' : 'Family Membership (2 Years)'],
                    ['Spouse', selected.spouse_name || '—'],
                    ['Children', selected.children?.length ? selected.children.join(', ') : '—'],
                    ['Payment', selected.payment_status],
                    ['Paid at', fmtDate(selected.paid_at)],
                    ['Expires at', fmtDate(selected.expires_at)],
                    ['Registered', fmtDate(selected.created_at)],
                  ].map(([k, v]) => (
                    <><dt key={k} className="col-4 small text-muted">{k}</dt><dd key={k + 'v'} className="col-8 small">{v || '—'}</dd></>
                  ))}
                </dl>
              </div>
              <div className="modal-footer">
                {isAdmin && (
                  <button className="btn btn-outline-secondary btn-sm rounded-pill" onClick={() => { setSelected(null); openEdit(selected); }}>
                    <i className="fas fa-pencil-alt me-1"></i>Edit
                  </button>
                )}
                <button className="btn btn-secondary btn-sm rounded-pill" onClick={() => setSelected(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit modal ── */}
      {editingMember && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060 }} onClick={closeEdit}>
          <div className="modal-dialog modal-dialog-centered modal-xl" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Edit Member —{' '}
                  <span className="text-muted fw-normal">{editingMember.first_name} {editingMember.last_name}</span>
                  {editingMember.membership_no && (
                    <span className="badge bg-light text-secondary ms-2 fw-normal" style={{ fontSize: '0.85rem' }}>{editingMember.membership_no}</span>
                  )}
                </h5>
                <button className="btn-close" onClick={closeEdit}></button>
              </div>
              <form onSubmit={saveEdit}>
                <div className="modal-body">
                  {editError && <div className="alert alert-danger py-2 mb-3">{editError}</div>}
                  <MemberFormFields values={editForm} onChange={(k, v) => setEditForm(f => ({ ...f, [k]: v }))} />
                </div>
                <div className="modal-footer">
                  <button type="submit" disabled={editSaving} className="btn btn-primary rounded-pill px-5">
                    {editSaving ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</> : <><i className="fas fa-save me-2"></i>Save Changes</>}
                  </button>
                  <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={closeEdit}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirmation modal (two steps) ── */}
      {deleteTarget && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.55)', zIndex: 1070 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 420 }}>
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title text-danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {deleteStep === 1 ? 'Delete Member?' : 'Are you absolutely sure?'}
                </h5>
              </div>
              <div className="modal-body pt-2">
                {deleteStep === 1 ? (
                  <p className="mb-0">
                    You are about to delete <strong>{deleteTarget.first_name} {deleteTarget.last_name}</strong>
                    {deleteTarget.membership_no && <> ({deleteTarget.membership_no})</>}.
                    <br /><span className="text-muted small">Their membership record will be permanently removed.</span>
                  </p>
                ) : (
                  <>
                    <p className="mb-2">This <strong className="text-danger">cannot be undone</strong>. Confirm permanent deletion of:</p>
                    <div className="alert alert-danger py-2 mb-0">
                      <strong>{deleteTarget.first_name} {deleteTarget.last_name}</strong>
                      {deleteTarget.membership_no && <span className="ms-2 small text-muted">{deleteTarget.membership_no}</span>}
                      {deleteTarget.email && <div className="small mt-1">{deleteTarget.email}</div>}
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer border-0 pt-0 gap-2">
                <button className="btn btn-outline-secondary rounded-pill px-4" onClick={cancelDelete} disabled={deleting}>Cancel</button>
                <button className="btn btn-danger rounded-pill px-4" onClick={confirmDelete} disabled={deleting}>
                  {deleting
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Deleting…</>
                    : deleteStep === 1 ? 'Yes, Delete' : 'Permanently Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
